import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
  error: '',
  success: '',
  loading: false,
  showOldPassword: false,
  showNewPassword: false,
  showConfirmPassword: false,
};

export const changePassword = createAsyncThunk(
  'userProfile/changePassword',
  async ({ userId, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const updatePayload = { oldPassword, newPassword };
      const response = await api.put(`/users/${userId}/change-password`, updatePayload);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update profile. Please try again.';
      return rejectWithValue({ message });
    }
  }
);

const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    setOldPassword(state, action) {
      state.oldPassword = action.payload;
    },
    setNewPassword(state, action) {
      state.newPassword = action.payload;
    },
    setConfirmPassword(state, action) {
      state.confirmPassword = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setSuccess(state, action) {
      state.success = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setShowOldPassword(state, action) {
      state.showOldPassword = action.payload;
    },
    setShowNewPassword(state, action) {
      state.showNewPassword = action.payload;
    },
    setShowConfirmPassword(state, action) {
      state.showConfirmPassword = action.payload;
    },
    resetStatus(state) {
      state.error = '';
      state.success = '';
    },
    resetForm(state) {
      state.oldPassword = '';
      state.newPassword = '';
      state.confirmPassword = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = '';
        state.success = '';
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.success = 'Password updated successfully!';
        state.oldPassword = '';
        state.newPassword = '';
        state.confirmPassword = '';
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update profile. Please try again.';
      });
  },
});

export const {
  setOldPassword,
  setNewPassword,
  setConfirmPassword,
  setError,
  setSuccess,
  setLoading,
  setShowOldPassword,
  setShowNewPassword,
  setShowConfirmPassword,
  resetStatus,
  resetForm,
} = userProfileSlice.actions;

export const selectUserProfileOldPassword = (state) => state.userProfile.oldPassword;
export const selectUserProfileNewPassword = (state) => state.userProfile.newPassword;
export const selectUserProfileConfirmPassword = (state) =>
  state.userProfile.confirmPassword;
export const selectUserProfileError = (state) => state.userProfile.error;
export const selectUserProfileSuccess = (state) => state.userProfile.success;
export const selectUserProfileLoading = (state) => state.userProfile.loading;
export const selectUserProfileShowOldPassword = (state) => state.userProfile.showOldPassword;
export const selectUserProfileShowNewPassword = (state) => state.userProfile.showNewPassword;
export const selectUserProfileShowConfirmPassword = (state) =>
  state.userProfile.showConfirmPassword;

export default userProfileSlice.reducer;
